import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ProductCard } from '../components/ProductCard';
import { MagnifyingGlass, CaretLeft, CaretRight, X } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PRODUCTS_PER_PAGE = 12;

export const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);

  const fetchProducts = useCallback(async (page = 1, search = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(PRODUCTS_PER_PAGE) });
      if (search) params.append('search', search);
      const response = await axios.get(`${API}/products?${params}`);
      setProducts(response.data.products);
      setTotalPages(response.data.pages);
      setTotalProducts(response.data.total);
      setCurrentPage(response.data.page);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts(currentPage, activeSearch);
  }, [currentPage, activeSearch, fetchProducts]);

  const handleSearch = (e) => {
    e.preventDefault();
    setActiveSearch(searchQuery);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setActiveSearch('');
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen" data-testid="shop-page">
      {/* Header */}
      <div className="px-6 md:px-12 lg:px-24 py-12 border-b border-border">
        <h1 
          className="text-5xl sm:text-7xl uppercase tracking-tighter font-bold"
          style={{ fontFamily: "'Oswald', sans-serif" }}
          data-testid="shop-title"
        >
          ALL PRODUCTS
        </h1>
        <p className="text-muted-foreground mt-2" data-testid="product-count">
          {totalProducts} PRODUCTS
        </p>
      </div>

      <div className="px-6 md:px-12 lg:px-24 py-8">
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-8" data-testid="search-form">
          <div className="flex gap-2 max-w-lg">
            <div className="flex-1 relative">
              <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="SEARCH PRODUCTS..."
                className="w-full bg-transparent border border-border pl-11 pr-10 py-3 text-sm uppercase tracking-wide placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                data-testid="search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
                  data-testid="clear-search-btn"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            <Button
              type="submit"
              className="bg-white text-black hover:bg-primary hover:text-white transition-colors px-6 h-auto"
              data-testid="search-submit-btn"
            >
              SEARCH
            </Button>
          </div>
          {activeSearch && (
            <p className="text-xs text-muted-foreground mt-3 uppercase tracking-wide">
              Results for "<span className="text-white">{activeSearch}</span>" — {totalProducts} found
            </p>
          )}
        </form>

        {/* Product Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
            {[...Array(6)].map((_, i) => (
              <div key={`skeleton-${i}`} className="border border-border">
                <div className="aspect-[3/4] bg-muted animate-pulse" />
                <div className="p-4 border-t border-border space-y-2">
                  <div className="h-4 bg-muted animate-pulse w-3/4" />
                  <div className="h-3 bg-muted animate-pulse w-1/2" />
                  <div className="h-4 bg-muted animate-pulse w-1/4" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground text-lg">NO PRODUCTS FOUND</p>
            {activeSearch && (
              <button
                onClick={clearSearch}
                className="mt-4 text-xs uppercase tracking-wide text-primary hover:underline"
                data-testid="clear-search-results"
              >
                CLEAR SEARCH
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1" data-testid="product-grid">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12 pt-8 border-t border-border" data-testid="pagination">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="border-border hover:bg-primary hover:text-white hover:border-primary disabled:opacity-30"
                  data-testid="pagination-prev"
                >
                  <CaretLeft size={18} />
                </Button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <Button
                    key={page}
                    variant={page === currentPage ? "default" : "outline"}
                    size="icon"
                    onClick={() => goToPage(page)}
                    className={page === currentPage 
                      ? "bg-white text-black hover:bg-primary hover:text-white" 
                      : "border-border hover:bg-primary hover:text-white hover:border-primary"
                    }
                    data-testid={`pagination-page-${page}`}
                  >
                    {page}
                  </Button>
                ))}
                
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="border-border hover:bg-primary hover:text-white hover:border-primary disabled:opacity-30"
                  data-testid="pagination-next"
                >
                  <CaretRight size={18} />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
